import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Carrossel de banners publicados pelo admin.
 * - Fetch de /public/banners (só ativos, ordenados)
 * - Autoplay 6s, pausa no hover
 * - Setas laterais + dots inferiores
 * - Não renderiza se lista vazia (evita espaço em branco)
 */
export default function BannerCarousel() {
    const [banners, setBanners] = useState([]);
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        let mounted = true;
        api.get("/public/banners")
            .then((r) => { if (mounted) setBanners(r.data || []); })
            .catch(() => setBanners([]));
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        if (banners.length <= 1 || paused) return;
        const t = setInterval(() => setIndex((i) => (i + 1) % banners.length), 6000);
        return () => clearInterval(t);
    }, [banners.length, paused]);

    const current = useMemo(() => banners[index], [banners, index]);
    if (banners.length === 0) return null;

    const go = (dir) => setIndex((i) => (i + dir + banners.length) % banners.length);

    return (
        <section
            className="bg-white border-b border-neutral-200"
            data-testid="banner-carousel"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
                <div className="relative overflow-hidden bg-neutral-100 aspect-[21/9]">
                    {banners.map((b, i) => (
                        <div
                            key={b.id}
                            aria-hidden={i !== index}
                            className={`absolute inset-0 transition-opacity duration-500 ${i === index ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                        >
                            <img
                                src={b.image_url}
                                alt={b.title}
                                loading={i === 0 ? "eager" : "lazy"}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                            />
                        </div>
                    ))}

                    {banners.length > 1 && (
                        <>
                            <button
                                aria-label="Banner anterior"
                                data-testid="banner-prev"
                                onClick={() => go(-1)}
                                className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 bg-white/90 hover:bg-white text-ink grid place-items-center shadow-lg"
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <button
                                aria-label="Próximo banner"
                                data-testid="banner-next"
                                onClick={() => go(1)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 bg-white/90 hover:bg-white text-ink grid place-items-center shadow-lg"
                            >
                                <ChevronRight size={20} />
                            </button>
                        </>
                    )}
                </div>

                {(current?.title || current?.subtitle) && (
                    <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
                        <div>
                            {current?.title && (
                                <div className="text-xs font-mono uppercase tracking-[0.24em] text-brand-blue" data-testid="banner-title">{current.title}</div>
                            )}
                            {current?.subtitle && (
                                <div className="mt-1 text-sm text-neutral-600" data-testid="banner-subtitle">{current.subtitle}</div>
                            )}
                        </div>
                        {banners.length > 1 && (
                            <div className="flex items-center gap-2" data-testid="banner-dots">
                                {banners.map((_, i) => (
                                    <button
                                        key={i}
                                        aria-label={`Ir para banner ${i + 1}`}
                                        onClick={() => setIndex(i)}
                                        className={`h-1 transition-all duration-300 ${i === index ? "w-8 bg-brand-blue" : "w-3 bg-neutral-300 hover:bg-neutral-400"}`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}
